import { useEffect, useState } from "react"
import { Navigate, Route, Routes } from "react-router-dom"
import { createAppSession, createBridge, type AppBridge, type AppClient, type AppContext } from "@koortex/apps-sdk"
import { ADMIN_TABS, PillNav, TabNav } from "@/components/tab-nav"
import { useHostTheme } from "@/lib/host-theme"
import { Planning } from "@/pages/Planning"
import { Dispatch } from "@/pages/Dispatch"
import { DayPlan } from "@/pages/DayPlan"
import { Orders } from "@/pages/Orders"
import { Fleet } from "@/pages/Fleet"
import { Products } from "@/pages/Products"
import { PostalCodes } from "@/pages/PostalCodes"

export interface AppShell {
  context: AppContext
  client: AppClient
  bridge: AppBridge
  /** Role numérico do token do shell (10 admin, 5 gestor, 3 utilizador). */
  role: number
  toast(kind: "success" | "error" | "info", title: string, description?: string): void
}

function decodeRoleFromToken(token: string): number {
  try {
    const body = token.split(".")[1]
    if (!body) return 3
    const normalized = body.replace(/-/g, "+").replace(/_/g, "/")
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "=")
    const parsed = JSON.parse(atob(padded)) as { role?: unknown }
    return typeof parsed.role === "number" && Number.isInteger(parsed.role) ? parsed.role : 3
  } catch {
    return 3
  }
}

/**
 * O host abre a app num iframe com `?token=…`. `createAppSession` dá um cliente
 * estável que renova o token pelo host (o token de 30 min vai no URL e recarregar
 * o iframe não o renova).
 */
function useAppShell(): AppShell | null {
  const [shell, setShell] = useState<AppShell | null>(null)
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("token")
    if (!token) {
      console.warn("[logistics] Sem token no URL — a correr fora do Koortex.")
      return
    }
    try {
      const session = createAppSession(token)
      const bridge = createBridge({ allowedCapabilities: ["toast", "navigate.crm", "context.read", "files.upload"] })
      setShell({
        context: session.context,
        client: session.client,
        bridge,
        role: decodeRoleFromToken(token),
        toast: (variant, title, description) => bridge.toast({ variant, title, description }),
      })
    } catch (err) {
      console.error("[logistics] Token inválido:", err)
    }
  }, [])
  return shell
}

/** Definições (transportadoras, frota, códigos postais): só administradores. */
function Settings({ children }: { children: React.ReactNode }) {
  return (
    <div>
      <div className="px-4 pt-1">
        <PillNav tabs={ADMIN_TABS} size="sm" />
      </div>
      {children}
    </div>
  )
}

export function App() {
  const shell = useAppShell()
  useHostTheme(shell?.context.hostOrigin)
  const isAdmin = (shell?.role ?? 0) >= 10
  const adminOnly = (el: React.ReactElement) => (isAdmin ? <Settings>{el}</Settings> : <Navigate to="/despacho" replace />)
  return (
    <div className="flex h-screen w-full flex-col bg-background text-foreground">
      <TabNav isAdmin={isAdmin} />
      <main className="flex-1 overflow-auto">
        {!shell ? (
          <div className="p-8 text-sm text-muted-foreground">A abrir… (a app só funciona dentro do Koortex)</div>
        ) : (
          <Routes>
            <Route path="/despacho" element={<Dispatch shell={shell} />} />
            <Route path="/planeamento" element={<DayPlan shell={shell} />} />
            <Route path="/planeamento/quadro" element={<Planning shell={shell} />} />
            <Route path="/encomendas" element={<Orders shell={shell} />} />
            <Route path="/produtos" element={<Products shell={shell} />} />
            <Route path="/definicoes/transportadoras" element={adminOnly(<Fleet shell={shell} />)} />
            <Route path="/definicoes/codigos-postais" element={adminOnly(<PostalCodes shell={shell} />)} />
            <Route path="/definicoes" element={<Navigate to="/definicoes/transportadoras" replace />} />
            <Route path="/transportadoras" element={<Navigate to="/definicoes/transportadoras" replace />} />
            <Route path="/codigos-postais" element={<Navigate to="/definicoes/codigos-postais" replace />} />
            <Route path="*" element={<Navigate to={`/despacho${window.location.search}`} replace />} />
          </Routes>
        )}
      </main>
    </div>
  )
}
